/**
 * OIMS v2 - AWS tab (IP Whitelisting + Instance Scheduling)
 */

import { escapeHtml } from './utils.js';
import { showApprovalRequested } from './approvals.js';

// ── IP Whitelisting state ────────────────────────────────────────
let ipInput, selInstance, selPort, btnGrant, grantsBody;
let customExpiryInput, confirmModal, revokeModal;
let instances = [];
let activeGrants = [];
let refreshInterval = null;
let pendingGrantBody = null;
let pendingRevokeGrantId = null;
let ipIsAdmin = false;  // admin group — can use longer durations + extend beyond 4h

// ── Instance Scheduling state ────────────────────────────────────
let selSchedInstance, btnStartInstance, schedulesBody;
let schedConfirmModal, schedStopModal;
let schedInstances = [];
let pendingStartBody = null;
let pendingStopInstanceId = null;
let pendingStopScheduleId = null;
let schedCountdownInterval = null;
let awsState = null;
let schedIsAdmin = false;  // admin group only — can schedule "forever"

export function initAws(state) {
  awsState = state;
  // ── IP Whitelisting DOM refs ──
  ipInput = document.getElementById('awsIpAddress');
  selInstance = document.getElementById('awsInstance');
  selPort = document.getElementById('awsPort');
  btnGrant = document.getElementById('btnGrantIp');
  grantsBody = document.getElementById('grantsBody');
  customExpiryInput = document.getElementById('awsCustomExpiry');

  if (selInstance && btnGrant) {
    // Confirm modal
    const confirmModalEl = document.getElementById('confirmGrantModal');
    if (confirmModalEl) {
      confirmModal = new bootstrap.Modal(confirmModalEl);
      document.getElementById('btnConfirmGrant')?.addEventListener('click', () => {
        confirmModal.hide();
        executeGrant();
      });
    }

    // Wire events
    selInstance.addEventListener('change', () => onInstanceChange());
    btnGrant.addEventListener('click', () => showGrantConfirmation());

    document.getElementById('btnRefreshIp')?.addEventListener('click', () => detectMyIp());
    document.getElementById('btnRefreshGrants')?.addEventListener('click', () => loadGrants());

    // Duration radio → show/hide custom datetime + set max to 24h from now
    document.querySelectorAll('input[name="awsDuration"]').forEach(radio => {
      radio.addEventListener('change', () => {
        if (customExpiryInput) {
          customExpiryInput.style.display = radio.value === 'custom' ? '' : 'none';
          if (radio.value === 'custom') {
            const maxDate = new Date(Date.now() + 24 * 3600000);
            customExpiryInput.max = maxDate.toISOString().slice(0, 16);
            const minDate = new Date(Date.now() + 60000);
            customExpiryInput.min = minDate.toISOString().slice(0, 16);
          }
        }
      });
    });

    // Update grant button state when port changes
    // (instance change is handled by onInstanceChange which calls updateGrantButton)
    selPort.addEventListener('change', updateGrantButton);
  }

  // ── Instance Scheduling DOM refs ──
  selSchedInstance = document.getElementById('schedInstance');
  btnStartInstance = document.getElementById('btnStartInstance');
  schedulesBody = document.getElementById('schedulesBody');

  if (selSchedInstance && btnStartInstance) {
    const startModalEl = document.getElementById('confirmStartModal');
    if (startModalEl) {
      schedConfirmModal = new bootstrap.Modal(startModalEl);
      document.getElementById('btnConfirmStart')?.addEventListener('click', () => {
        schedConfirmModal.hide();
        executeStart();
      });
    }

    const stopModalEl = document.getElementById('confirmStopModal');
    if (stopModalEl) {
      schedStopModal = new bootstrap.Modal(stopModalEl);
      document.getElementById('btnConfirmStop')?.addEventListener('click', () => {
        schedStopModal.hide();
        if (pendingStopScheduleId) {
          executeScheduleStop(pendingStopScheduleId);
        } else if (pendingStopInstanceId) {
          executeDirectStop(pendingStopInstanceId);
        }
      });
    }

    const revokeModalEl = document.getElementById('confirmRevokeModal');
    if (revokeModalEl) {
      revokeModal = new bootstrap.Modal(revokeModalEl);
      document.getElementById('btnConfirmRevoke')?.addEventListener('click', () => {
        revokeModal.hide();
        if (pendingRevokeGrantId) {
          executeRevoke(pendingRevokeGrantId);
        }
      });
    }

    selSchedInstance.addEventListener('change', () => onSchedInstanceChange());
    btnStartInstance.addEventListener('click', () => showStartConfirmation());

    document.getElementById('btnRefreshSchedules')?.addEventListener('click', () => loadSchedules());

    // Duration radios update max info + show/hide custom input
    document.querySelectorAll('input[name="schedDuration"]').forEach(radio => {
      radio.addEventListener('change', () => {
        const customGroup = document.getElementById('schedCustomGroup');
        if (customGroup) customGroup.style.display = radio.value === 'custom' ? '' : 'none';
        updateStartButton();
      });
    });
  }

  // Tab focus → refresh both sections
  document.addEventListener('oims:tab-change', (e) => {
    if (e.detail?.tab === 'aws') {
      // Ensure the active sub-tab pane is visible (Bootstrap strips show/active on nested panes)
      const activeSubTab = document.querySelector('#awsSubTabs .nav-link.active');
      if (activeSubTab) {
        const target = document.querySelector(activeSubTab.dataset.bsTarget);
        if (target && !target.classList.contains('show')) {
          target.classList.add('show', 'active');
        }
      }
      loadGrants();
      loadSchedules();
      startAutoRefresh();
    } else {
      stopAutoRefresh();
    }
  });

  // Refresh on approval updates
  document.addEventListener('oims:approval_update', (e) => {
    if (e.detail?.action_id === 'allow_ip' && e.detail?.status === 'completed') {
      loadGrants();
    }
    if (e.detail?.action_id === 'schedule_instance' && e.detail?.status === 'completed') {
      loadSchedules();
    }
  });

  // Listen for SSE schedule updates (warnings, stops)
  document.addEventListener('oims:schedule_update', (e) => {
    const d = e.detail;
    if (d?.event === 'warning') {
      window.toast?.warning(`${d.instance_name} will stop in 15 minutes`);
    } else if (d?.event === 'stopped') {
      window.toast?.info(`${d.instance_name} has been stopped (${d.reason || 'auto'})`);
    } else if (d?.event === 'started') {
      window.toast?.success(`${d.instance_name} started — auto-stop at ${d.auto_stop_display || ''}`);
    } else if (d?.event === 'failed') {
      window.toast?.error(`${d.instance_name} action failed: ${d.reason || 'unknown'}`);
    }
    loadSchedules();
  });
}

export async function loadAwsData() {
  await Promise.all([
    detectMyIp(),
    loadInstances(),
    loadGrants(),
    loadSchedulableInstances(),
    loadSchedules(),
  ]);
}

// ═══════════════════════════════════════════════════════════════════
// IP WHITELISTING (existing)
// ═══════════════════════════════════════════════════════════════════

async function detectMyIp() {
  try {
    const { data } = await axios.get('/aws/my-ip');
    if (ipInput) {
      ipInput.value = data.ip || '';
      ipInput.setAttribute('readonly', '');
    }
  } catch (e) {
    console.error('detectMyIp error', e);
  }
}

async function loadInstances() {
  try {
    const { data } = await axios.get('/aws/sg/instances');
    instances = data.instances || [];
    ipIsAdmin = !!data.is_admin;

    // Show/hide admin-only duration options
    document.querySelectorAll('.ipAdminOnly').forEach(el => {
      el.style.display = ipIsAdmin ? '' : 'none';
    });

    if (!selInstance) return;
    selInstance.innerHTML = '<option value="">Select instance...</option>';
    instances.forEach(inst => {
      const opt = document.createElement('option');
      opt.value = inst.instance_id;
      opt.textContent = inst.name;
      selInstance.appendChild(opt);
    });
  } catch (e) {
    console.error('loadInstances error', e);
  }
}

function onInstanceChange() {
  const instId = selInstance?.value;
  const inst = instances.find(i => i.instance_id === instId);

  // Always clear duplicate hint on instance change
  const dupHint = document.getElementById('grantDuplicateHint');
  if (dupHint) dupHint.style.display = 'none';

  if (!selPort) return;
  selPort.innerHTML = '';
  selPort.disabled = true;

  if (!inst || !inst.ports?.length) {
    selPort.innerHTML = '<option value="">Select instance first...</option>';
    updateGrantButton();
    return;
  }

  selPort.disabled = false;
  inst.ports.forEach(p => {
    const opt = document.createElement('option');
    opt.value = p.port;
    opt.textContent = `${p.port} - ${p.label} (${p.protocol})`;
    selPort.appendChild(opt);
  });
  updateGrantButton();
}

function updateGrantButton() {
  if (!btnGrant) return;
  const hasInstance = selInstance?.value;
  const hasPort = selPort?.value;
  const hasIp = ipInput?.value;

  // Check if user already has active grant for this server:port
  const port = parseInt(hasPort, 10);
  const existing = hasInstance && hasPort && activeGrants.find(
    g => g.instance_id === hasInstance && g.port === port
  );

  const dupHint = document.getElementById('grantDuplicateHint');
  if (existing) {
    btnGrant.disabled = true;
    if (dupHint) { dupHint.textContent = 'You already have active access to this server/port'; dupHint.style.display = 'block'; }
  } else {
    btnGrant.disabled = !(hasInstance && hasPort && hasIp);
    if (dupHint) dupHint.style.display = 'none';
  }
}

function showGrantConfirmation() {
  const ip = ipInput?.value?.trim();
  const instanceId = selInstance?.value;
  const port = parseInt(selPort?.value, 10);

  if (!ip || !instanceId || !port) {
    window.toast?.warning('Please fill all fields');
    return;
  }

  // Get duration (admin=24h, normal=4h)
  const MAX_GRANT_HOURS = ipIsAdmin ? 24 : 4;
  const durRadio = document.querySelector('input[name="awsDuration"]:checked');
  let durationMinutes = null;
  let expiresAt = null;
  let durationLabel = '';

  const maxExpiry = new Date(Date.now() + MAX_GRANT_HOURS * 3600000);

  if (durRadio?.value === 'custom') {
    expiresAt = customExpiryInput?.value;
    if (!expiresAt) {
      window.toast?.warning('Please select a custom expiry time');
      return;
    }
    let customDate = new Date(expiresAt);
    if (customDate > maxExpiry) {
      customDate = maxExpiry;
      expiresAt = maxExpiry.toISOString();
      window.toast?.warning(`Capped to maximum ${MAX_GRANT_HOURS}h`);
    }
    durationLabel = customDate.toLocaleString();
  } else if (durRadio?.value === 'eod') {
    const now = new Date();
    let eod = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 0);
    if (eod > maxExpiry) {
      eod = maxExpiry;
      window.toast?.warning(`Capped to maximum ${MAX_GRANT_HOURS}h`);
    }
    expiresAt = eod.toISOString();
    durationLabel = 'End of day (' + eod.toLocaleTimeString() + ')';
  } else {
    durationMinutes = parseInt(durRadio?.value || '60', 10);
    if (durationMinutes > MAX_GRANT_HOURS * 60) {
      durationMinutes = MAX_GRANT_HOURS * 60;
      window.toast?.warning(`Capped to maximum ${MAX_GRANT_HOURS}h`);
    }
    const hrs = Math.floor(durationMinutes / 60);
    durationLabel = hrs > 0 ? `${hrs}h ${durationMinutes % 60}m` : `${durationMinutes}m`;
  }

  // Build pending body
  pendingGrantBody = { instance_id: instanceId, port };
  if (durationMinutes) pendingGrantBody.duration_minutes = durationMinutes;
  if (expiresAt) pendingGrantBody.expires_at = expiresAt;

  // Populate modal
  const inst = instances.find(i => i.instance_id === instanceId);
  const portCfg = inst?.ports?.find(p => p.port === port);

  document.getElementById('confirmGrantIp').textContent = ip;
  document.getElementById('confirmGrantInstance').textContent = inst?.name || instanceId;
  document.getElementById('confirmGrantPort').textContent = portCfg
    ? `${port} - ${portCfg.label} (${portCfg.protocol})`
    : String(port);
  document.getElementById('confirmGrantDuration').textContent = durationLabel;

  confirmModal?.show();
}

async function executeGrant() {
  btnGrant.disabled = true;
  btnGrant.classList.add('btn-loading');

  try {
    const { data } = await axios.post('/aws/sg/grant', pendingGrantBody);

    // Maker/Checker: action requires approval
    if (data.status === 'approval_required') {
      window.toast?.info(data.message || 'Approval required for IP whitelisting.');
      showApprovalRequested(data);
      return;
    }

    window.toast?.success('IP access granted successfully');
    await loadGrants();
  } catch (e) {
    const msg = e.response?.data?.detail || e.message || 'Grant failed';
    window.toast?.error(msg);
  } finally {
    btnGrant.disabled = false;
    btnGrant.classList.remove('btn-loading');
    updateGrantButton();
    pendingGrantBody = null;
  }
}

async function loadGrants() {
  try {
    const { data } = await axios.get('/aws/sg/grants');
    const grants = data.grants || [];
    // Track own active grants for duplicate check
    // is_own flag is set by the backend for grants belonging to current user
    activeGrants = grants.filter(g => g.status === 'active' && g.is_own);
    renderGrants(grants);
    updateGrantButton();
  } catch (e) {
    console.error('loadGrants error', e);
  }
}

function renderGrants(grants) {
  if (!grantsBody) return;

  if (!grants.length) {
    grantsBody.innerHTML = '<tr><td colspan="7" class="text-center text-muted py-3">No grants</td></tr>';
    return;
  }

  grantsBody.innerHTML = grants.map(g => {
    const isActive = g.status === 'active';
    const exp = new Date(g.expires_at);
    const now = new Date();
    const remaining = exp - now;

    let expiryText;
    if (!isActive) {
      expiryText = g.revoke_reason || g.status;
    } else if (remaining <= 0) {
      expiryText = 'Expiring...';
    } else {
      const mins = Math.floor(remaining / 60000);
      const hrs = Math.floor(mins / 60);
      expiryText = hrs > 0 ? `${hrs}h ${mins % 60}m` : `${mins}m`;
    }

    const statusBadge = isActive
      ? '<span class="badge bg-success">Active</span>'
      : g.status === 'expired'
        ? '<span class="badge bg-secondary">Expired</span>'
        : '<span class="badge bg-warning text-dark">Revoked</span>';

    // Action buttons
    let actionBtns = '';
    if (isActive) {
      // Extend button: show if own grant or admin, and not at max
      if (g.is_own || ipIsAdmin) {
        const maxH = ipIsAdmin ? 24 : 4;
        const created = new Date(g.created_at);
        const maxExp = new Date(created.getTime() + maxH * 3600000);
        const canExtend = exp.getTime() + 3600000 <= maxExp.getTime();
        if (canExtend) {
          actionBtns += `<button class="btn btn-sm btn-outline-primary me-1" onclick="window._extendGrant('${escapeHtml(g.grant_id)}')" title="Extend +1h">
            <i class="bi bi-plus-circle"></i> 1h
          </button>`;
        }
      }
      actionBtns += `<button class="btn btn-sm btn-outline-danger" onclick="window._revokeGrant('${escapeHtml(g.grant_id)}', '${escapeHtml(g.instance_name)}', '${escapeHtml(g.ip_address)}', '${g.port} ${escapeHtml(g.port_label || '')}')" title="Revoke">
           <i class="bi bi-x-circle"></i>
         </button>`;
    }

    // Shorten email: show user part only
    const userShort = g.user_display || (g.user_email || '').split('@')[0];

    return `<tr class="${isActive ? '' : 'opacity-50'}">
      <td title="${escapeHtml(g.user_email)}">${escapeHtml(userShort)}</td>
      <td><code>${escapeHtml(g.ip_address)}</code></td>
      <td>${escapeHtml(g.instance_name)}</td>
      <td>${g.port} <small class="text-muted">${escapeHtml(g.port_label || '')}</small></td>
      <td>${escapeHtml(expiryText)}</td>
      <td>${statusBadge}</td>
      <td>${actionBtns}</td>
    </tr>`;
  }).join('');
}

// Global revoke handler (called from onclick in table)
window._revokeGrant = function(grantId, instanceName, ip, portLabel) {
  pendingRevokeGrantId = grantId;
  document.getElementById('confirmRevokeInstance').textContent = instanceName || 'Instance';
  document.getElementById('confirmRevokeIp').textContent = ip || '';
  document.getElementById('confirmRevokePort').textContent = portLabel || '';
  revokeModal?.show();
};

async function executeRevoke(grantId) {
  try {
    await axios.delete(`/aws/sg/grants/${grantId}`);
    window.toast?.success('Grant revoked');
    await loadGrants();
  } catch (e) {
    const msg = e.response?.data?.detail || 'Revoke failed';
    window.toast?.error(msg);
  } finally {
    pendingRevokeGrantId = null;
  }
}

window._extendGrant = async function(grantId) {
  try {
    await axios.post(`/aws/sg/grants/${grantId}/extend`, { extra_hours: 1 });
    window.toast?.success('Extended by 1 hour');
    await loadGrants();
  } catch (e) {
    const detail = e.response?.data?.detail || 'Extension failed';
    window.toast?.error(detail);
    // Refresh to hide button if at max
    await loadGrants();
  }
};

// ═══════════════════════════════════════════════════════════════════
// INSTANCE SCHEDULING
// ═══════════════════════════════════════════════════════════════════

async function loadSchedulableInstances() {
  try {
    const { data } = await axios.get('/aws/schedule/instances');
    schedInstances = data.instances || [];
    schedIsAdmin = !!data.is_admin;

    // Show/hide forever option based on admin status
    const foreverRadio = document.getElementById('schedDurForever');
    const foreverLabel = document.getElementById('schedDurForeverLabel');
    if (foreverRadio && foreverLabel) {
      foreverRadio.style.display = schedIsAdmin ? '' : 'none';
      foreverLabel.style.display = schedIsAdmin ? '' : 'none';
    }

    if (!selSchedInstance) return;
    selSchedInstance.innerHTML = '<option value="">Select instance...</option>';
    schedInstances.forEach(inst => {
      const stateEmoji = inst.state === 'running' ? '\u{1F7E2}' : inst.state === 'stopped' ? '\u{1F534}' : '\u26AA';
      const opt = document.createElement('option');
      opt.value = inst.instance_id;
      opt.textContent = `${stateEmoji} ${inst.name}`;
      opt.dataset.maxHours = inst.max_runtime_hours || 168;
      opt.dataset.state = inst.state || 'unknown';
      selSchedInstance.appendChild(opt);
    });
  } catch (e) {
    // User may not have schedule_instance permission — hide section silently
    if (e.response?.status === 403) return;
    console.error('loadSchedulableInstances error', e);
  }
}

function onSchedInstanceChange() {
  const instId = selSchedInstance?.value;
  const inst = schedInstances.find(i => i.instance_id === instId);

  const stateGroup = document.getElementById('schedStateGroup');
  const stateBadge = document.getElementById('schedStateBadge');
  const maxInfo = document.getElementById('schedMaxInfo');
  const durationGroup = document.getElementById('schedDurationGroup');

  if (!inst) {
    if (stateGroup) stateGroup.style.display = 'none';
    if (maxInfo) maxInfo.textContent = '';
    if (durationGroup) durationGroup.style.display = '';
    updateStartButton();
    return;
  }

  const isRunning = inst.state === 'running';

  // Show current state
  if (stateGroup && stateBadge) {
    stateGroup.style.display = '';
    const stateColors = {
      running: 'bg-success',
      stopped: 'bg-danger',
      stopping: 'bg-warning text-dark',
      pending: 'bg-info',
    };
    const cls = stateColors[inst.state] || 'bg-secondary';
    stateBadge.innerHTML = `<span class="badge ${cls}">${inst.state || 'unknown'}</span>`;
  }

  // Hide duration when running (stop doesn't need duration)
  if (durationGroup) durationGroup.style.display = isRunning ? 'none' : '';

  // Update button text based on state
  if (btnStartInstance) {
    if (isRunning) {
      btnStartInstance.innerHTML = '<i class="bi bi-stop-fill me-2"></i>Stop Now';
      btnStartInstance.classList.remove('btn-primary');
      btnStartInstance.classList.add('btn-danger');
    } else {
      btnStartInstance.innerHTML = '<i class="bi bi-play-fill me-2"></i>Start Now';
      btnStartInstance.classList.remove('btn-danger');
      btnStartInstance.classList.add('btn-primary');
    }
  }

  // Show max runtime info and cap duration radios
  const maxHrs = inst.max_runtime_hours || 168;
  if (maxInfo) maxInfo.textContent = `Max runtime: ${maxHrs}h (${Math.round(maxHrs / 24)}d)`;

  // Update custom hours max
  const customInput = document.getElementById('schedCustomHours');
  if (customInput) customInput.max = maxHrs;

  // Disable duration options that exceed max
  document.querySelectorAll('input[name="schedDuration"]').forEach(radio => {
    if (radio.value === 'max' || radio.value === 'custom' || radio.value === 'forever') return;
    const hrs = parseFloat(radio.value);
    const label = radio.nextElementSibling;
    if (hrs > maxHrs) {
      radio.disabled = true;
      if (label) label.classList.add('opacity-50');
      if (radio.checked) {
        const first = document.querySelector('input[name="schedDuration"]:not(:disabled)');
        if (first) first.checked = true;
      }
    } else {
      radio.disabled = false;
      if (label) label.classList.remove('opacity-50');
    }
  });

  updateStartButton();
}

function updateStartButton() {
  if (!btnStartInstance) return;
  btnStartInstance.disabled = !selSchedInstance?.value;
}

function getSchedDurationHours() {
  const radio = document.querySelector('input[name="schedDuration"]:checked');
  if (!radio) return 1;
  if (radio.value === 'forever') return 0;
  if (radio.value === 'max') {
    const inst = schedInstances.find(i => i.instance_id === selSchedInstance?.value);
    return inst?.max_runtime_hours || 168;
  }
  if (radio.value === 'custom') {
    const input = document.getElementById('schedCustomHours');
    const val = parseFloat(input?.value);
    const inst = schedInstances.find(i => i.instance_id === selSchedInstance?.value);
    const maxHrs = inst?.max_runtime_hours || 168;
    return (val > 0 && val <= maxHrs) ? val : 1;
  }
  return parseFloat(radio.value);
}

function showStartConfirmation() {
  const instanceId = selSchedInstance?.value;
  if (!instanceId) {
    window.toast?.warning('Please select an instance');
    return;
  }

  const inst = schedInstances.find(i => i.instance_id === instanceId);
  const isRunning = inst?.state === 'running';

  if (isRunning) {
    // Show stop confirmation modal
    pendingStopInstanceId = instanceId;
    pendingStopScheduleId = null;
    document.getElementById('confirmStopInstance').textContent = inst?.name || instanceId;
    schedStopModal?.show();
    return;
  }

  const durationHours = getSchedDurationHours();

  pendingStartBody = {
    instance_id: instanceId,
    duration_hours: durationHours,
  };

  // Populate modal
  document.getElementById('confirmStartInstance').textContent = inst?.name || instanceId;
  document.getElementById('confirmStartDuration').textContent = durationHours === 0 ? 'Forever' : `${durationHours}h`;

  if (durationHours === 0) {
    document.getElementById('confirmStartAutoStop').textContent = 'Manual stop only';
  } else {
    const autoStopTime = new Date(Date.now() + durationHours * 3600000);
    document.getElementById('confirmStartAutoStop').textContent = autoStopTime.toLocaleTimeString();
  }

  schedConfirmModal?.show();
}

async function executeDirectStop(instanceId) {
  btnStartInstance.disabled = true;
  btnStartInstance.classList.add('btn-loading');

  try {
    await axios.post('/aws/schedule/stop-instance', { instance_id: instanceId });
    window.toast?.success('Instance stop initiated');
    await loadSchedules();
    await loadSchedulableInstances();
  } catch (e) {
    const msg = e.response?.data?.detail || e.message || 'Stop failed';
    window.toast?.error(msg);
  } finally {
    btnStartInstance.disabled = false;
    btnStartInstance.classList.remove('btn-loading');
    pendingStopInstanceId = null;
    updateStartButton();
  }
}

async function executeScheduleStop(scheduleId) {
  try {
    await axios.post(`/aws/schedule/${scheduleId}/stop`);
    window.toast?.success('Instance stopped');
    await loadSchedules();
    await loadSchedulableInstances();
  } catch (e) {
    const msg = e.response?.data?.detail || 'Stop failed';
    window.toast?.error(msg);
  } finally {
    pendingStopScheduleId = null;
  }
}

async function executeStart() {
  btnStartInstance.disabled = true;
  btnStartInstance.classList.add('btn-loading');

  try {
    const { data } = await axios.post('/aws/schedule/start', pendingStartBody);

    if (data.status === 'approval_required') {
      window.toast?.info(data.message || 'Approval required for instance scheduling.');
      showApprovalRequested(data);
      return;
    }

    window.toast?.success('Instance start initiated');
    await loadSchedules();
    await loadSchedulableInstances();
  } catch (e) {
    const msg = e.response?.data?.detail || e.message || 'Start failed';
    window.toast?.error(msg);
  } finally {
    btnStartInstance.disabled = false;
    btnStartInstance.classList.remove('btn-loading');
    updateStartButton();
    pendingStartBody = null;
  }
}

async function loadSchedules() {
  try {
    const { data } = await axios.get('/aws/schedule/jobs');
    const schedules = data.schedules || [];
    renderSchedules(schedules);
  } catch (e) {
    if (e.response?.status === 403) return;
    console.error('loadSchedules error', e);
  }
}

function renderSchedules(schedules) {
  if (!schedulesBody) return;

  // Only show recent/active schedules (pending, running, or completed/failed within last hour)
  const cutoff = Date.now() / 1000 - 3600;
  const visible = schedules.filter(s =>
    s.status === 'pending' || s.status === 'running' ||
    (s.stopped_at && s.stopped_at > cutoff) ||
    (s.created_at > cutoff)
  );

  if (!visible.length) {
    schedulesBody.innerHTML = '<tr><td colspan="6" class="text-center text-muted py-3">No active schedules</td></tr>';
    return;
  }

  schedulesBody.innerHTML = visible.map(s => {
    const isActive = s.status === 'running';
    const isPending = s.status === 'pending';

    // Status badge
    const statusMap = {
      pending: '<span class="badge bg-info">Pending</span>',
      running: '<span class="badge bg-success">Running</span>',
      completed: '<span class="badge bg-secondary">Stopped</span>',
      cancelled: '<span class="badge bg-warning text-dark">Cancelled</span>',
      failed: '<span class="badge bg-danger">Failed</span>',
    };
    const statusBadge = statusMap[s.status] || `<span class="badge bg-secondary">${escapeHtml(s.status)}</span>`;

    // Started time
    const startedText = s.started_at
      ? new Date(s.started_at * 1000).toLocaleTimeString()
      : (isPending ? 'Scheduled' : '-');

    // Auto-stop countdown
    let autoStopText = '-';
    const isForever = s.auto_stop_at === 0;
    if (isForever && (isActive || isPending)) {
      autoStopText = '<span class="text-info fw-bold">Forever</span>';
    } else if (isActive || isPending) {
      const remaining = s.auto_stop_at * 1000 - Date.now();
      if (remaining > 0) {
        const mins = Math.floor(remaining / 60000);
        const hrs = Math.floor(mins / 60);
        const rmins = mins % 60;
        autoStopText = hrs > 0 ? `${hrs}h ${rmins}m` : `${rmins}m`;
        if (mins <= 15) {
          autoStopText = `<span class="text-warning fw-bold">${autoStopText}</span>`;
        }
      } else {
        autoStopText = '<span class="text-danger">Stopping...</span>';
      }
    } else if (s.stop_reason) {
      autoStopText = escapeHtml(s.stop_reason);
    }

    // Action buttons
    let actions = '';
    if (isActive) {
      actions = `
        <button class="btn btn-sm btn-outline-danger me-1" onclick="window._stopSchedule('${escapeHtml(s.schedule_id)}', '${escapeHtml(s.instance_name)}')" title="Stop Now">
          <i class="bi bi-stop-fill"></i>
        </button>`;
      if (!isForever) {
        actions += `
        <button class="btn btn-sm btn-outline-primary me-1" onclick="window._extendSchedule('${escapeHtml(s.schedule_id)}')" title="Extend +1h">
          <i class="bi bi-plus-circle"></i> 1h
        </button>`;
        if (schedIsAdmin) {
          actions += `
          <button class="btn btn-sm btn-outline-info" onclick="window._makeForever('${escapeHtml(s.schedule_id)}', '${escapeHtml(s.instance_name)}')" title="Make Forever">
            <i class="bi bi-infinity"></i>
          </button>`;
        }
      }
    } else if (isPending) {
      actions = `
        <button class="btn btn-sm btn-outline-secondary" onclick="window._cancelSchedule('${escapeHtml(s.schedule_id)}')" title="Cancel">
          <i class="bi bi-x-circle"></i>
        </button>`;
    }

    const userLabel = s.user_display || (s.user_email || '').split('@')[0];
    const opacity = (isActive || isPending) ? '' : 'opacity-50';

    return `<tr class="${opacity}">
      <td>${escapeHtml(s.instance_name)}</td>
      <td title="${escapeHtml(s.user_email)}">${escapeHtml(userLabel)}</td>
      <td>${statusBadge}</td>
      <td>${startedText}</td>
      <td>${autoStopText}</td>
      <td class="text-nowrap">${actions}</td>
    </tr>`;
  }).join('');
}

// Global schedule action handlers
window._stopSchedule = function(scheduleId, instanceName) {
  pendingStopInstanceId = null;
  pendingStopScheduleId = scheduleId;
  document.getElementById('confirmStopInstance').textContent = instanceName || 'Instance';
  schedStopModal?.show();
};

window._extendSchedule = async function(scheduleId) {
  try {
    await axios.post(`/aws/schedule/${scheduleId}/extend`, { extra_hours: 1 });
    window.toast?.success('Extended by 1 hour');
    await loadSchedules();
  } catch (e) {
    const msg = e.response?.data?.detail || 'Extension failed';
    window.toast?.error(msg);
  }
};

window._makeForever = async function(scheduleId, instanceName) {
  if (!confirm(`Make ${instanceName} run forever (no auto-stop)?`)) return;
  try {
    await axios.post(`/aws/schedule/${scheduleId}/extend`, { forever: true, extra_hours: 0 });
    window.toast?.success('Schedule set to forever');
    await loadSchedules();
  } catch (e) {
    const msg = e.response?.data?.detail || 'Failed to set forever';
    window.toast?.error(msg);
  }
};

window._cancelSchedule = async function(scheduleId) {
  if (!confirm('Cancel this scheduled start?')) return;
  try {
    await axios.delete(`/aws/schedule/${scheduleId}`);
    window.toast?.success('Schedule cancelled');
    await loadSchedules();
  } catch (e) {
    const msg = e.response?.data?.detail || 'Cancel failed';
    window.toast?.error(msg);
  }
};

// ═══════════════════════════════════════════════════════════════════
// AUTO-REFRESH (shared)
// ═══════════════════════════════════════════════════════════════════

function startAutoRefresh() {
  stopAutoRefresh();
  refreshInterval = setInterval(() => {
    loadGrants();
    loadSchedules();
  }, 30000);
}

function stopAutoRefresh() {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
}
